import React, { useEffect, useState, useRef } from 'react'
import { getPersonalityData } from 'services/break.js'
import ReferralCard from './components/ReferralCardNoVoting'
import ReferralCardScreen from './components/ReferralCardScreen' // confusing - this has voting, didn't want to break anything
import ShareIcons from 'components/ShareIcons'
import WistiaEmbed from 'steps/video/WistiaEmbed'
import eventEmitter from 'steps/shared/eventEmitter'
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
  adaptV4Theme,
} from '@mui/material/styles'
import { fontFamily } from 'global/_js-variables'

import LegacyRetinaImage from 'components/LegacyRetinaImage'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'

import './ReferralStep.scss'

import analytics from 'global/AnalyticsData'

/*
Referral is one of a few steps that can show personalized reults based on a Personality Quiz result. 
There's code in there that tries to get the user's PQ result in order to filter
*/

const ReferralStep = ({
  brk,
  breakId,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  onAuthRequired,
  onUnhandledError,
  skipButton,
  nextStepButton,
  // puck injection props (BR-5452) — all defaulted so legacy breaks are untouched
  isPuck = false,
  puckCardStyle = undefined,
  puckCardOverlayStyle = undefined,
  puckHeadlineStyle = undefined,
  puckHeader = null,
  puckFooter = null,
  // PUCK-BACKEND-GAP (BR-5452): no beaker referral entity behind a puck step,
  // so votes are tallied locally (mockVoting). Remove once the backend
  // supports puck referral voting.
  mockVoting = false,
}) => {
  //  let sessionPqResultId = sessionStorageWrapper.getItem(`${breakId}_pq_result_id`)
  const [pqResultId, setPqResultId] = useState(null)

  const hasVote =
    step.links.some(link => link.hasGoal) ||
    step.referralData.voteType === 'ALL_ITEMS' ||
    step.referralData.voteType === 'PER_ITEM'

  const sectionEl = useRef(null)

  let ReferralObject = step.referralData
  ReferralObject.links = step.links
  const layout = ReferralObject.layout
  const dropdownLinks = ReferralObject.links.filter(
    link => link.buttonType === 'MENU',
  )
  const buttonLinks = ReferralObject.links.filter(
    link => link.buttonType === 'BUTTON',
  )

  const [inView, entry] = useIntersectionObserver(sectionEl, {
    threshold: 0.1,
  })

  useEffect(() => {
    const asyncWrapper = async () => {
      if (inView) {
        await getPersonalityQuizResultId()
      }
    }
    asyncWrapper()
  }, [inView])

  useEffect(() => {
    eventEmitter.on('setPQResult', msg => {
      setPqResultId(msg)
    })

    // Cleanup on unmount
    return () => {
      eventEmitter.off('setPQResult', () => {})
    }
  }, [])

  const getPersonalityQuizResultId = async () => {
    try {
      const resultId = await getPersonalityData(breakId)
      setPqResultId(resultId)
    } catch (e) {
      console.log(e)
      if (e.message === '401') {
        onAuthRequired && onAuthRequired()
      } else {
        onUnhandledError && onUnhandledError()
      }
    }
  }

  const closeDropdowns = e => {
    const openDropdowns = [
      ...document.getElementsByClassName('dropdown-cover open'),
    ]
    if (!e.target.matches('.dropdown-card *')) {
      !!openDropdowns.length &&
        openDropdowns.forEach(dropdown => dropdown.classList.remove('open'))
    }
  }

  const setInteractedAndUnlock = () => {
    onEngage()
    onAwardPoints()
  }

  const handleLinkClick = link => {
    if (typeof window !== `undefined`) {

      analytics.stepEvent('step_interaction_referral_click', step, {
        result_id: link.id,
        result: link.url,
      })
    }

    setInteractedAndUnlock()
  }

  const handleShareClick = ({ platform }) => {
    analytics.stepEvent('step_interaction_referral_share_option', step, {
      result_id: platform,
      result: platform,
    })
  }

  const handleVideoComponentLoad = () => {
    console.log(`handleVideoComponentLoad`)
  }

  const handleVideoViewed = () => {
    console.log(`handleVideoViewed`)
  }

  const handleVideoPlayed = (videoId, videoName) => {
    console.log(`handleVideoPlayed`)
  }

  const handleVideoWatched = (videoId, videoName) => {
    console.log(`handleVideoWatched`)
  }

  const referralTheme = createTheme(
    adaptV4Theme({
      typography: {
        fontFamily: fontFamily,
      },
    }),
  )

  const dropdownOpenHandler = function (id, text, url) {
      analytics.stepEvent('step_interaction_referral_select', step, {
        result_id: id,
        result: text,
      })
  }

  const linkClickHandler = function (id, text, url) {
      analytics.stepEvent('step_interaction_referral_select_click', step, {
        result_id: id,
        result: url,
      })
      setInteractedAndUnlock && setInteractedAndUnlock()
  }

  // referral items can be filtered bassed on PQ results.
  // TODO: someone comment this logic. the "not this or not that" return statement is not intuitive
//   console.log(`filtered links has result id: `, pqResultId)
  const filteredLinks = ReferralObject.links
    ? ReferralObject.links.filter((link, i) => {
        const hide =
          link.dynamics &&
          link.dynamics.some(dynamic => dynamic.value === pqResultId)

        return !link.dynamics || !hide
      })
    : []

  // find out how many tiles will appear. used to add a css classes to help with "dangling" items
  const visibleCount = filteredLinks.length

  // how many cards will be left on the final row?
  let remainder = 0
  if (layout === 'TWO_COLUMN') {
    remainder = visibleCount % 2
  }

  if (layout === 'THREE_COLUMN') {
    remainder = visibleCount % 3
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={referralTheme}>
        <section
          ref={sectionEl}
          className={`referral-step ${isPuck ? 'puck-referral-step' : ''}`}
        >
          <div
            className={`step-content with-shadow ${
              brk?.navigationType === 'TOP' ? 'top-navigation' : ''
            }`}
            style={puckCardStyle}
            onClick={closeDropdowns.bind(this)}
          >
            {puckCardOverlayStyle && <div style={puckCardOverlayStyle} />}
            {ReferralObject.headline && (
              <h1
                className={`step-content__title referral-title`}
                style={puckHeadlineStyle}
                dangerouslySetInnerHTML={{ __html: ReferralObject.headline }}
              />
            )}
            {/* To do: simplify and remove this missioncolor use her so we don't need to pass a brk prop */}
            {ReferralObject.introCopy && (
              <h2
                className="step-intro step-subcopy"
                dangerouslySetInnerHTML={{ __html: ReferralObject.introCopy }}
              />
            )}

            {ReferralObject && ReferralObject.videoId && (
              <div className="referral-video">
                <div className="wistia_responsive_wrapper">
                  <WistiaEmbed
                    onLoaded={handleVideoComponentLoad}
                    videoIDs={[ReferralObject.videoId]}
                    handleVideoViewed={handleVideoViewed}
                    handleVideoPlayed={handleVideoPlayed}
                    handleVideoWatched={handleVideoWatched}
                    autoPlay={false}
                  />
                </div>
              </div>
            )}

            {puckHeader}

            {/* The single column layout is a completely different UI. It has weird buisiness logic in beaker */}
            {layout === 'ONE_COLUMN' && (
              <div className={`layout-${layout}`}>
                {ReferralObject.headerImage && (
                  <div className="referral-image-single">
                    {/* TODO - smelly: either use better validation or change the backend */}
                    {ReferralObject.links[0]?.url &&
                    ReferralObject.links[0].url !== 'https://' ? (
                      <a
                        href={ReferralObject.links[0].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        // The handler takes the link, not the click event:
                        // passing it bare reported result and result_id as
                        // undefined, which reaches the warehouse as null
                        // (BR-2211). Report the same link the href points at.
                        onClick={() =>
                          handleLinkClick(ReferralObject.links[0])
                        }
                      >
                        <LegacyRetinaImage image={ReferralObject.headerImage} />
                      </a>
                    ) : (
                      <LegacyRetinaImage image={ReferralObject.headerImage} />
                    )}
                  </div>
                )}
                <div
                  className={`link-list count-${
                    visibleCount % 2 === 0 ? 'even' : 'odd'
                  } total-count-${visibleCount}`}
                >
                  {filteredLinks.map((link, i) => {
                    return (
                      <ReferralCard
                        key={i}
                        link={link}
                        i={filteredLinks.length - i - 1}
                        layout={layout}
                        dropdownOpenHandler={dropdownOpenHandler}
                        linkClickHandler={linkClickHandler}
                        handleLinkClick={handleLinkClick}
                      />
                    )
                  })}
                </div>
              </div>
            )}

            {layout !== 'ONE_COLUMN' && (
              <div className={`layout-${layout}`}>
                {!hasVote && (
                  <div className={`card-list remainder-${remainder}`}>
                    {filteredLinks &&
                      filteredLinks.map((link, i) => {
                        return (
                          <ReferralCard
                            key={i}
                            link={link}
                            i={filteredLinks.length - i - 1}
                            layout={layout}
                            dropdownOpenHandler={dropdownOpenHandler}
                            linkClickHandler={linkClickHandler}
                            handleLinkClick={handleLinkClick}
                          />
                        )
                      })}
                  </div>
                )}
                {hasVote && filteredLinks.length > 0 && (
                  <ReferralCardScreen
                    referralLinks={filteredLinks}
                    referralId={step.referralData.id}
                    onLinkClick={handleLinkClick}
                    layout={layout}
                    voteType={step.referralData.voteType}
                    mockVoting={mockVoting}
                  />
                )}
              </div>
            )}
            <div
              className="referral-bottom-text"
              dangerouslySetInnerHTML={{ __html: ReferralObject.bottomText }}
            ></div>
            {hasVote && step.socialShareOn && (
              <ShareIcons
                facebookShare={step.facebookShare}
                twitterShare={step.twitterShare}
                pinterestShare={step.pinterestShare}
                emailShare={step.emailShare}
                emailConfig={{
                  subject: step.shareEmailSubject,
                  body: step.shareEmailBody,
                }}
                copyShare={step.copyShare}
                whatsappShare={step.whatsappShare}
                shareUrl={brk ? brk.shareUrl : ''}
                onShareClick={handleShareClick}
              />
            )}
            {puckFooter}
            {skipButton}
            {nextStepButton}
          </div>
        </section>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default ReferralStep
